import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSDataGrid } from '../../../shared/components/table';
import { RMSTextField, RMSSelect, RMSCheckbox } from '../../../shared/components/form-controls';
import { RMSButton } from '../../../shared/components/buttons';
import { RMSIconButton } from '../../../shared/components/buttons/index';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { addDictionaryValue, updateDictionaryValue } from 'reducers/DictionaryReducer';
import addCitySchema from '../../schema/addCitySchema';
import {
  DEFAULT_VALUES,
  DICTIONARY_CATEGORY,
  DICT_SELECT_PROPS,
  CITY_GRID_COLUMNS,
} from '../../constants/addCityFormConstants';
import { DEFAULT_DICTIONARY_PERMISSIONS } from '../../constants/dictionaryValueFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import SectionLabel from './SectionLabel';
import {
  getCities,
  createCityRecord,
  updateCityRecord,
  deleteCityRecord,
} from '../../services/userService';

const CityManagementDialog = ({ open, onClose, onCityCreated }) => {
  const dispatch = useDispatch();
  const cityDictionary = useSelector((state) => state.dictionary[DICTIONARY_CATEGORY.CITY] || []);
  const permissions = useSelector(
    (state) =>
      state.permissions?.dictionary?.[DICTIONARY_CATEGORY.CITY] || DEFAULT_DICTIONARY_PERMISSIONS
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cities, setCities] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [defaultDisabled, setDefaultDisabled] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const methods = useForm({
    resolver: CreateFormResolver(addCitySchema),
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const { stateOptions } = useUserDictionaryOptions();
  const editingCityID = useWatch({ control: methods.control, name: 'ptsCityID' });
  const isEditing = Boolean(editingCityID);

  const visibleCities = useMemo(() => cities.filter((row) => !row.IsDeleted), [cities]);

  const loadCities = useCallback(async () => {
    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getCities(),
      onSuccess: (data) => setCities(data),
    });
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;
    loadCities();
  }, [open, loadCities]);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingRow(null);
    methods.reset(DEFAULT_VALUES);
  }, [methods]);

  const requestFormClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this city. Are you sure you want to close without saving?',
        () => handleFormClose(),
        'Unsaved Changes'
      );
      return;
    }

    handleFormClose();
  }, [isDirty, requestConfirm, handleFormClose]);

  const requestDialogClose = useCallback(() => {
    if (formOpen && isDirty) {
      requestConfirm(
        'You have unsaved changes on this city. Are you sure you want to close without saving?',
        () => {
          handleFormClose();
          onClose?.();
        },
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [formOpen, isDirty, requestConfirm, handleFormClose, onClose]);

  const handleAdd = useCallback(() => {
    const defaultExists = visibleCities.some((row) => row.IsDefault);
    setDefaultDisabled(defaultExists);
    setEditingRow(null);
    methods.reset(DEFAULT_VALUES);
    setFormOpen(true);
  }, [visibleCities, methods]);

  const handleEdit = useCallback(
    (row) => {
      const defaultRow = visibleCities.find((r) => r.IsDefault);
      setDefaultDisabled(Boolean(defaultRow && defaultRow.ptsCityID !== row.ptsCityID));
      setEditingRow(row);
      methods.reset({
        ptsCityID: row.ptsCityID,
        CityDescription: row.CityDescription || '',
        State: row.State || '',
        Category: row.Category || '',
        IsDefault: Boolean(row.IsDefault),
        IsActive: Boolean(row.IsActive),
        IsDeleted: Boolean(row.IsDeleted),
      });
      setFormOpen(true);
    },
    [visibleCities, methods]
  );

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete "${row.CityDescription}"?`,
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () => deleteCityRecord(row.ptsCityID),
            onSuccess: () => {
              dispatch(notify('City deleted successfully', 'success'));

              const remaining = cityDictionary.filter((r) => r.ptsCityID !== row.ptsCityID);
              dispatch(updateDictionaryValue(remaining, DICTIONARY_CATEGORY.CITY));

              loadCities();
            },
          });
        },
        'Delete City'
      );
    },
    [dispatch, requestConfirm, cityDictionary, loadCities]
  );

  const onSubmit = async (data) => {
    const isEdit = Boolean(data.ptsCityID);
    const payload = {
      CityDescription: data.CityDescription,
      State: data.State || null,
      Category: data.Category || null,
      IsDefault: data.IsDefault,
      IsActive: data.IsActive,
      IsDeleted: data.IsDeleted,
    };

    await executeApiAction({
      setLoading: setSaving,
      dispatch,
      action: () =>
        isEdit ? updateCityRecord(data.ptsCityID, payload) : createCityRecord(payload),
      onSuccess: (city) => {
        dispatch(
          notify(isEdit ? 'City updated successfully' : 'City created successfully', 'success')
        );

        if (isEdit) {
          const updated = cityDictionary.map((row) =>
            row.ptsCityID === city.ptsCityID ? city : row
          );
          dispatch(updateDictionaryValue(updated, DICTIONARY_CATEGORY.CITY));
          handleFormClose();
          loadCities();
        } else {
          dispatch(addDictionaryValue(city, DICTIONARY_CATEGORY.CITY));
          onCityCreated?.(city);
          onClose?.();
        }
      },
    });
  };

  const handleSave = methods.handleSubmit(onSubmit);

  const renderRowActions = useCallback(
    (row) => (
      <>
        {permissions.Edit && (
          <RMSIconButton icon={<EditIcon />} tooltip="Edit" onClick={() => handleEdit(row)} />
        )}
        {permissions.Delete && (
          <RMSIconButton
            className="delete-icon"
            icon={<DeleteIcon />}
            tooltip="Delete"
            onClick={() => handleDelete(row)}
          />
        )}
      </>
    ),
    [permissions, handleEdit, handleDelete]
  );

  return (
    <RMSCommonDialog
      open={open}
      title="City"
      onClose={requestDialogClose}
      loading={loading || saving}
      maxWidth="md"
      fullWidth
      closeOnBackdropClick={false}
      className="city-management-dialog"
      saveButton={{ show: false }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}>
      {formOpen && (
        <Box className="rms-dialog-section">
          <SectionLabel>{isEditing ? 'Edit City' : 'Add City'}</SectionLabel>
          <FormProvider {...methods}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <RMSTextField name="CityDescription" label="City" />
              </Grid>
              <Grid item xs={4}>
                <RMSSelect
                  name="State"
                  label="State"
                  options={stateOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={4}>
                <RMSTextField name="Category" label="Category" />
              </Grid>
              <Grid item xs={4}>
                <RMSCheckbox name="IsDefault" label="Is Default" disabled={defaultDisabled} />
              </Grid>
              <Grid item xs={4}>
                <RMSCheckbox name="IsActive" label="Is Active" />
              </Grid>
              <Grid item xs={4}>
                <RMSCheckbox name="IsDeleted" label="Is Deleted" />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              {isEditing ? (
                <RMSRecordHistoryButton
                  disabled={!editingRow?.Created}
                  created={editingRow?.Created}
                  createdBy={editingRow?.CreatedBy}
                  updated={editingRow?.Updated}
                  updatedBy={editingRow?.UpdatedBy}
                />
              ) : (
                <span />
              )}
              <Box display="flex">
                <RMSButton
                  label="Save"
                  variant="contained"
                  color="primary"
                  prefixIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                />
                <Box ml={1}>
                  <RMSButton
                    label="Close"
                    variant="outlined"
                    color="secondary"
                    onClick={requestFormClose}
                  />
                </Box>
              </Box>
            </Box>
          </FormProvider>
        </Box>
      )}

      <Box className="city-management-dialog__grid-wrap">
        <RMSDataGrid
          primaryKey="ptsCityID"
          columns={CITY_GRID_COLUMNS}
          rows={visibleCities}
          renderActions={renderRowActions}
          enableColumnResize
          enableColumnReorder
          toolbar={{
            showToolbar: true,
            searchable: true,
            actions: {
              showAddButton: permissions.Create,
              onAdd: handleAdd,
            },
          }}
        />
      </Box>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </RMSCommonDialog>
  );
};

export default CityManagementDialog;
